import { createSelector } from 'reselect';
import type { RootState } from 'store';
import { sortStorageUnits } from 'util/storageUnitSort';
import { StorageUnitStatus, StorageUnitType } from './types';
import { splitName } from 'util/nameHelper';
import { getCurrentUserId } from 'auth/userIdentity';
import { getIsOnline } from 'store/connection/selectors';

export const getActiveFilters = (state: RootState) =>
  state.storageUnitsState.filters;

export const getAdvancedFilters = (state: RootState) =>
  state.storageUnitsState.advancedFilters;

export const getStorageUnits = (state: RootState) =>
  state.storageUnitsState.storageUnits;

export const getArchived = (state: RootState) =>
  state.storageUnitsState.archived;

export const getApprovedFiles = (state: RootState) =>
  state.storageUnitsState.approvedFiles;

export const getRecentFiles = (state: RootState) =>
  state.storageUnitsState.recentFiles;

export const getSearchString = (state: RootState) =>
  state.storageUnitsState.searchString;

export const getTemplates = (state: RootState) =>
  state.storageUnitsState.templates;

export const getLoading = (state: RootState) => state.storageUnitsState.loading;

export const getSort = (state: RootState) => state.storageUnitsState.sort;

export const getSortType = (state: RootState) =>
  state.storageUnitsState.sort.type;

export const getSortDirection = (state: RootState) =>
  state.storageUnitsState.sort.direction;

export const getHasMoreDataToRetrieve = (state: RootState) =>
  state.storageUnitsState.moreDataToRetrieve;

export const getCurrentUploads = (state: RootState) =>
  state.storageUnitsState.currentUploads;

export const getStorageUnitsForDashboard = createSelector(
  [getStorageUnits, getSort],
  (storageUnits, sort) => {
    return sortStorageUnits([...storageUnits], sort);
  },
);

export const getArchivedForDashboard = createSelector(
  [getArchived, getSort],
  (storageUnits, sort) => {
    return sortStorageUnits(storageUnits, sort);
  },
);

export const getTemplatesForDashboard = createSelector(
  [getTemplates, getSort],
  (templates, sort) => {
    return sortStorageUnits(templates, sort);
  },
);

export const getApprovedFilesForDashboard = createSelector(
  [getApprovedFiles, getSort],
  (files, sort) => {
    return sortStorageUnits(files, sort);
  },
);

export const getCurrentFile = (state: RootState) =>
  state.storageUnitsState.currentFile;

export const getCurrentFileIsApproved = createSelector(
  [getCurrentFile],
  (currentFile) => currentFile.status.includes(StorageUnitStatus.APPROVED),
);

export const getFileName = createSelector([getCurrentFile], (c) => c.name);

export const getFileNameWithoutEnding = createSelector([getCurrentFile], (c) =>
  splitName(StorageUnitType.File, c.name),
);

export const getFileId = createSelector([getCurrentFile], (c) => c.id);

export const getHasLockedFiles = createSelector(
  [getStorageUnits, getIsOnline],
  (units, isOnline) => {
    if (!isOnline) {
      return false;
    }
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      return false;
    }

    return units.some(
      (unit) =>
        unit.type === StorageUnitType.File &&
        unit.exclusiveLease?.userId === currentUserId,
    );
  },
);
